"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AnimatePresence, motion } from "framer-motion";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { WaveLeaderboardTime } from "@/components/waves/leaderboard/WaveLeaderboardTime";
import { WaveLeaderboardHeader } from "@/components/waves/leaderboard/header/WaveleaderboardHeader";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import { WaveLeaderboardDrops } from "@/components/waves/leaderboard/drops/WaveLeaderboardDrops";
import { WaveLeaderboardGallery } from "@/components/waves/leaderboard/gallery/WaveLeaderboardGallery";
import { WaveLeaderboardGrid } from "@/components/waves/leaderboard/grid/WaveLeaderboardGrid";
import { isLeaderboardViewMode, type LeaderboardViewMode } from "@/components/waves/leaderboard/types";
import { useWave } from "@/hooks/useWave";
import { useLayout } from "./layout/LayoutContext";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import useLocalPreference from "@/hooks/useLocalPreference";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useWaveCurationGroups } from "@/hooks/waves/useWaveCurationGroups";
interface MyStreamWaveLeaderboardProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveLeaderboard: React.FC<MyStreamWaveLeaderboardProps> = t0 => {
  const $ = _c(64);
  const {
    wave,
    onDropClick
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    isMemesWave
  } = useWave(wave);
  const {
    leaderboardViewStyle
  } = useLayout();
  const mountedRef = useRef(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      mountedRef.current = false;
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const [isCreatingDrop, setIsCreatingDrop] = useState(false);
  const viewPreferenceKey = `waveViewMode_${wave.id}`;
  const defaultViewMode = isMemesWave ? "grid" : "list";
  const [viewMode, setViewMode] = useLocalPreference(viewPreferenceKey, defaultViewMode, isLeaderboardViewMode);
  const sortPreferenceKey = `waveSortMode_${wave.id}`;
  const [sort, setSort] = useLocalPreference(sortPreferenceKey, WaveDropsLeaderboardSort.RANK, _temp);
  const t3 = wave.wave.type !== ApiWaveType.Chat;
  let t4;
  if ($[2] !== t3 || $[3] !== wave.id) {
    t4 = {
      waveId: wave.id,
      enabled: t3
    };
    $[2] = t3;
    $[3] = wave.id;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: t5,
    isLoading: isLoadingCurationGroups,
    isError: isCurationGroupsError
  } = useWaveCurationGroups(t4);
  let t6;
  if ($[5] !== t5) {
    t6 = t5 === undefined ? [] : t5;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const curationGroups = t6;
  let t7;
  if ($[7] !== searchParams) {
    t7 = searchParams.get("curated_by_group");
    $[7] = searchParams;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const rawCuratedByGroupId = t7;
  const curationGroupIdSet = new Set(curationGroups.map(_temp2));
  let t8;
  bb0: {
    if (!rawCuratedByGroupId) {
      t8 = undefined;
      break bb0;
    }
    if (isCurationGroupsError) {
      t8 = undefined;
      break bb0;
    }
    if (isLoadingCurationGroups) {
      t8 = rawCuratedByGroupId;
      break bb0;
    }
    t8 = curationGroupIdSet.has(rawCuratedByGroupId) ? rawCuratedByGroupId : undefined;
  }
  const curatedByGroupId = t8;
  let t9;
  if ($[9] !== pathname || $[10] !== router || $[11] !== searchParams) {
    t9 = groupId => {
      const nextParams = new URLSearchParams(searchParams.toString());
      if (groupId) {
        nextParams.set("curated_by_group", groupId);
      } else {
        nextParams.delete("curated_by_group");
      }
      const nextQuery = nextParams.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[9] = pathname;
    $[10] = router;
    $[11] = searchParams;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const updateCurationGroupInUrl = t9;
  let t10;
  bb1: {
    if (!isMemesWave) {
      t10 = viewMode;
      break bb1;
    }
    if (viewMode === "grid_content_only") {
      t10 = "grid";
      break bb1;
    }
    t10 = viewMode;
  }
  const effectiveViewMode = t10;
  let leaderboardContent;
  if (effectiveViewMode === "list") {
    let t11;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = () => {
        if (mountedRef.current) {
          setIsCreatingDrop(true);
        }
      };
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== curatedByGroupId || $[15] !== sort || $[16] !== wave) {
      t12 = <WaveLeaderboardDrops wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} onCreateDrop={t11} />;
      $[14] = curatedByGroupId;
      $[15] = sort;
      $[16] = wave;
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    leaderboardContent = t12;
  } else {
    if (!isMemesWave) {
      const t11 = effectiveViewMode === "grid" ? "compact" : "content_only";
      let t12;
      if ($[18] !== curatedByGroupId || $[19] !== onDropClick || $[20] !== sort || $[21] !== t11 || $[22] !== wave) {
        t12 = <WaveLeaderboardGrid wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} mode={t11} onDropClick={onDropClick} />;
        $[18] = curatedByGroupId;
        $[19] = onDropClick;
        $[20] = sort;
        $[21] = t11;
        $[22] = wave;
        $[23] = t12;
      } else {
        t12 = $[23];
      }
      leaderboardContent = t12;
    } else {
      let t11;
      if ($[24] !== curatedByGroupId || $[25] !== onDropClick || $[26] !== sort || $[27] !== wave) {
        t11 = <WaveLeaderboardGallery wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} onDropClick={onDropClick} />;
        $[24] = curatedByGroupId;
        $[25] = onDropClick;
        $[26] = sort;
        $[27] = wave;
        $[28] = t11;
      } else {
        t11 = $[28];
      }
      leaderboardContent = t11;
    }
  }
  let t11;
  if ($[29] !== wave) {
    t11 = <WaveLeaderboardTime wave={wave} />;
    $[29] = wave;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== setViewMode) {
    t12 = mode => setViewMode(mode);
    $[31] = setViewMode;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      if (mountedRef.current) {
        setIsCreatingDrop(true);
      }
    };
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== setSort) {
    t14 = s => setSort(s);
    $[34] = setSort;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = curatedByGroupId ?? null;
  const t16 = curationGroups.length > 0 ? updateCurationGroupInUrl : undefined;
  let t17;
  if ($[36] !== curationGroups || $[37] !== effectiveViewMode || $[38] !== sort || $[39] !== t12 || $[40] !== t14 || $[41] !== t15 || $[42] !== t16 || $[43] !== wave) {
    t17 = <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-black tw-py-4"><WaveLeaderboardHeader wave={wave} viewMode={effectiveViewMode} sort={sort} onViewModeChange={t12} onCreateDrop={t13} onSortChange={t14} curationGroups={curationGroups} curatedByGroupId={t15} onCurationGroupChange={t16} /></div>;
    $[36] = curationGroups;
    $[37] = effectiveViewMode;
    $[38] = sort;
    $[39] = t12;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = wave;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== isCreatingDrop || $[46] !== isMemesWave || $[47] !== wave) {
    t18 = isCreatingDrop && !isMemesWave && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><WaveDropCreate wave={wave} onCancel={() => {
        if (mountedRef.current) {
          setIsCreatingDrop(false);
        }
      }} onSuccess={() => {
        if (mountedRef.current) {
          setIsCreatingDrop(false);
        }
      }} /></motion.div>;
    $[45] = isCreatingDrop;
    $[46] = isMemesWave;
    $[47] = wave;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t18) {
    t19 = <AnimatePresence>{t18}</AnimatePresence>;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== isCreatingDrop || $[52] !== isMemesWave || $[53] !== wave) {
    t20 = isMemesWave && isCreatingDrop && <MemesArtSubmissionModal isOpen={isCreatingDrop} wave={wave} onClose={() => setIsCreatingDrop(false)} />;
    $[51] = isCreatingDrop;
    $[52] = isMemesWave;
    $[53] = wave;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== leaderboardContent || $[56] !== t19 || $[57] !== t20) {
    t21 = <div>{t19}{t20}{leaderboardContent}</div>;
    $[55] = leaderboardContent;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== leaderboardViewStyle || $[60] !== t11 || $[61] !== t17 || $[62] !== t21) {
    t22 = <div className="tw-w-full tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-flex-grow tw-px-2 sm:tw-px-4" style={leaderboardViewStyle}>{t11}{t17}{t21}</div>;
    $[59] = leaderboardViewStyle;
    $[60] = t11;
    $[61] = t17;
    $[62] = t21;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  return t22;
};
export default MyStreamWaveLeaderboard;
function _temp(value) {
  return value === WaveDropsLeaderboardSort.RANK || value === WaveDropsLeaderboardSort.RATING_PREDICTION || value === WaveDropsLeaderboardSort.TREND || value === WaveDropsLeaderboardSort.MY_REALTIME_VOTE || value === WaveDropsLeaderboardSort.CREATED_AT;
}
function _temp2(group) {
  return group.id;
}